// Proof photos: the database row and the file in private storage.
//
// ROW FIRST, FILE SECOND (CLAUDE.md invariant 8): the row is inserted as 'pending', the file is
// uploaded, then the row is flipped to 'ready'. The parent expense never waits on any of this.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'

export type AttachmentStatus = 'pending' | 'ready' | 'failed'

/**
 * What a photo is attached to: an expense (receipt), a remittance (deposit slip) or a collection sheet,
 * or, for the church treasury's own income, a counted service (the signed count sheet) or money sent
 * on (the slip). The last two belong to no department.
 */
export type ProofParent = { kind: 'expense' | 'movement' | 'sheet' | 'incomeSession' | 'incomeTransfer'; id: string }
const PARENT_COLUMN = {
  expense: 'expense_id',
  movement: 'movement_id',
  sheet: 'sheet_id',
  incomeSession: 'income_session_id',
  incomeTransfer: 'transfer_id',
} as const
/** Central proofs carry no department. */
export const isCentralParent = (p: ProofParent) => p.kind === 'incomeSession' || p.kind === 'incomeTransfer'

export interface Attachment {
  id: string
  parent: ProofParent
  status: AttachmentStatus
  thumbPath: string | null
  storagePath: string
  mimeType: string
  sizeBytes: number
  createdAt: string
}

type Row = {
  id: string
  status: AttachmentStatus
  thumb_path: string | null
  storage_path: string
  mime_type: string
  size_bytes: number
  created_at: string
}

export interface PendingAttachment {
  id: string
  /** null for the church treasury's own proofs (count sheets, transfer slips). */
  departmentId: string | null
  parent: ProofParent
  storagePath: string
  thumbPath: string
  mimeType: string
  sizeBytes: number
  sha256: string
}

/** Insert the 'pending' row. Safe to repeat: if it already exists from an earlier try, that is fine. */
export async function createPendingAttachment(a: PendingAttachment): Promise<void> {
  const userId = await currentUserId()
  const res = await supabase.from('attachment').insert({
    id: a.id,
    department_id: a.departmentId,
    [PARENT_COLUMN[a.parent.kind]]: a.parent.id,
    storage_path: a.storagePath,
    thumb_path: a.thumbPath,
    mime_type: a.mimeType,
    size_bytes: a.sizeBytes,
    sha256: a.sha256,
    uploaded_by: userId,
  })
  if (res.error && res.error.code !== '23505') throw res.error
}

/** The file is safely stored: flip the row to 'ready'. Safe to repeat. */
export async function markAttachmentReady(id: string): Promise<void> {
  const res = await supabase
    .from('attachment')
    .update({ status: 'ready', ready_at: new Date().toISOString() })
    .eq('id', id)
    .in('status', ['pending', 'failed'])
    .select('id')
  if (res.error) throw res.error
  if (((res.data as unknown[]) ?? []).length === 0) {
    // Nothing changed: fine if an earlier try already finished it, otherwise something is wrong and
    // the queue must keep the photo and try again rather than throw it away.
    const now = await supabase.from('attachment').select('status').eq('id', id).maybeSingle()
    if (now.error) throw now.error
    if ((now.data as { status?: string } | null)?.status !== 'ready') {
      throw { code: 'P0001', message: 'The photo record could not be marked as stored.' }
    }
  }
}

/** Upload one object. `upsert` makes a retry after an unclear failure harmless. */
export async function uploadProofObject(path: string, blob: Blob, contentType: string): Promise<void> {
  const res = await supabase.storage.from('proofs').upload(path, blob, { contentType, upsert: true })
  if (res.error) throw res.error
}

/** Photos recorded against an expense, a remittance or a collection sheet, oldest first. */
export async function listAttachments(parent: ProofParent): Promise<Attachment[]> {
  const res = await supabase
    .from('attachment')
    .select('id, status, thumb_path, storage_path, mime_type, size_bytes, created_at')
    .eq(PARENT_COLUMN[parent.kind], parent.id)
    .order('created_at')
  return (unwrap(res as never, 'Could not load the receipt photos.') as Row[]).map((r) => ({
    id: r.id,
    parent,
    status: r.status,
    thumbPath: r.thumb_path,
    storagePath: r.storage_path,
    mimeType: r.mime_type,
    sizeBytes: Number(r.size_bytes),
    createdAt: r.created_at,
  }))
}

/** A temporary link to a private file (the bucket is not public). */
export async function signedProofUrl(path: string, seconds = 3600): Promise<string> {
  const res = await supabase.storage.from('proofs').createSignedUrl(path, seconds)
  if (res.error || !res.data) throw new DataError(res.error, 'Could not open the photo.')
  return res.data.signedUrl
}
